import { useState, useMemo, useRef, useEffect } from "react";

import { useSlackChannels, useBroadcast } from "@/hooks/useSlackBroadcast";
import { useStore } from "@/lib/store";

interface BroadcastModalProps {
  message: string;
  onClose: () => void;
}

export function BroadcastModal({ message, onClose }: BroadcastModalProps) {
  const { data, isLoading, error } = useSlackChannels();
  const broadcast = useBroadcast();
  const showToast = useStore((state) => state.showToast);

  const [search, setSearch] = useState("");
  const [selectedChannel, setSelectedChannel] = useState<{ id: string; name: string } | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter channels based on search
  const filteredChannels = useMemo(() => {
    if (!data?.channels) return [];
    if (!search) return data.channels;
    const lower = search.toLowerCase();
    return data.channels.filter((ch) => ch.name.toLowerCase().includes(lower));
  }, [data?.channels, search]);

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredChannels]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = (channel: { id: string; name: string }) => {
    setSelectedChannel(channel);
    setSearch(channel.name);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, filteredChannels.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filteredChannels[highlightedIndex]) {
          handleSelect(filteredChannels[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  const handleSend = async () => {
    if (!selectedChannel) return;

    try {
      await broadcast.mutateAsync({ channel: selectedChannel.id, message });
      showToast("Broadcast sent!", "success");
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to broadcast";
      showToast(errorMessage, "error");
    }
  };

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close broadcast dialog"
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
      />

      {/* Centered modal */}
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
        <div className="border-cyber-border bg-cyber-surface pointer-events-auto w-80 rounded-lg border p-4">
          <h3 className="text-cyber-cyan mb-4 font-medium">Broadcast to Slack</h3>

          {isLoading ? (
            <div className="text-cyber-muted mb-4 text-sm">
              <span className="animate-spin-glow mr-2 inline-block">◐</span>
              Loading channels...
            </div>
          ) : error ? (
            <div className="text-cyber-red mb-4 text-sm">Failed to load channels</div>
          ) : (
            <div className="relative mb-4">
              <label className="text-cyber-muted mb-1 block text-xs">Channel</label>
              <div className="relative">
                <span className="text-cyber-muted absolute top-1/2 left-3 -translate-y-1/2">#</span>
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setSelectedChannel(null);
                    setIsOpen(true);
                  }}
                  onFocus={() => setIsOpen(true)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type to search..."
                  className="border-cyber-border bg-cyber-bg text-cyber-text focus:border-cyber-cyan/50 w-full rounded border py-2 pr-3 pl-7 text-sm focus:outline-none"
                  autoComplete="off"
                />
              </div>

              {/* Dropdown list */}
              {isOpen && filteredChannels.length > 0 && (
                <div
                  ref={listRef}
                  className="border-cyber-border bg-cyber-bg absolute top-full right-0 left-0 z-10 mt-1 max-h-48 overflow-y-auto rounded border shadow-lg"
                >
                  {filteredChannels.map((ch, index) => (
                    <button
                      type="button"
                      key={ch.id}
                      onClick={() => handleSelect(ch)}
                      className={`block w-full cursor-pointer px-3 py-2 text-left text-sm ${
                        index === highlightedIndex
                          ? "bg-cyber-cyan/20 text-cyber-cyan"
                          : "text-cyber-text hover:bg-cyber-cyan/10"
                      }`}
                    >
                      #{ch.name}
                    </button>
                  ))}
                </div>
              )}

              {isOpen && search && filteredChannels.length === 0 && (
                <div className="border-cyber-border bg-cyber-bg text-cyber-muted absolute top-full right-0 left-0 mt-1 rounded border px-3 py-2 text-sm">
                  No channels found
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="text-cyber-muted hover:text-cyber-text px-3 py-1.5 text-sm transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={!selectedChannel || broadcast.isPending}
              className="border-cyber-cyan/30 bg-cyber-cyan/10 text-cyber-cyan hover:border-cyber-cyan/50 hover:bg-cyber-cyan/20 rounded border px-3 py-1.5 text-sm transition-all disabled:cursor-not-allowed disabled:opacity-50"
            >
              {broadcast.isPending ? (
                <>
                  <span className="animate-spin-glow mr-1 inline-block">◐</span>
                  Sending...
                </>
              ) : (
                "Send"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
